import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Send,
  Refresh,
  Warning,
  ExpandMore,
  Download,
} from '@mui/icons-material';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import LoadingButton from '../../../Shared/Loading/index.tsx';
import { TemplateActions } from '../../../../Services/_actions/index.js';
import CommonBadges from '../../../Shared/Badges/index.jsx';
import CommonTable from '../../../Shared/Tables/TableV1.tsx';
import writeXlsxFile from 'write-excel-file';

const FilterStep = ({
  verifiedData,
  filteredData,
  setFilteredData,
  columnMapping,
  sheetColumns,
  filters,
  setFilters,
  templateData,
  uploadedFile,
  invalidRows = [],
  rowsWithAddedCode = [],
}) => {
  const { showSnackBar } = useSnackBar();
  const actions = TemplateActions();
  const [fieldFilters, setFieldFilters] = useState({});
  const [uniqueValues, setUniqueValues] = useState({});
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  // const [uploadedFile, setUploadedFile] = useState(null);
  const [isSendingTemplates, setIsSendingTemplates] = useState(false);

  // Extract unique values for each mapped column
  useEffect(() => {
    const values = {};
    columnMapping.forEach((mapping) => {
      if (mapping.sheetColumn) {
        const colIndex = sheetColumns.indexOf(mapping.sheetColumn);
        const uniqueSet = new Set();
        verifiedData.forEach((row) => {
          if (row[colIndex]) uniqueSet.add(String(row[colIndex]));
        });
        values[mapping.parameter] = Array.from(uniqueSet).sort();
      }
    });
    setUniqueValues(values);
  }, [verifiedData, columnMapping, sheetColumns]);

  // Reset filters
  const resetFilters = () => {
    setFieldFilters({});
    setFilteredData(verifiedData);
    showSnackBar('Filters reset', 'info');
  };

  // Handle field filter change
  const handleFieldFilterChange = (parameter, value) => {
    const newFilters = {
      ...fieldFilters,
      [parameter]: value,
    };
    setFieldFilters(newFilters);

    // Apply filters
    let filtered = [...verifiedData];

    Object.keys(newFilters).forEach((param) => {
      if (newFilters[param]) {
        const mapping = columnMapping.find(m => m.parameter === param);
        if (mapping) {
          const colIndex = sheetColumns.indexOf(mapping.sheetColumn);
          filtered = filtered.filter((row) => String(row[colIndex]) === newFilters[param]);
        }
      }
    });

    setFilteredData(filtered);
  };

  // Generate Excel file from data
  const generateFilteredExcelFile = async (data) => {
    try {
      const excelData = [];

      // Create headers with PARAMETER NAMES
      const headerRow = columnMapping
        .filter((m) => m.sheetColumn)
        .map((m) => ({
          value: m.parameter,
          fontWeight: 'bold',
        }));
      excelData.push(headerRow);

      // Add data rows
      data.forEach((row) => {
        const mappedRow = columnMapping
          .filter((m) => m.sheetColumn)
          .map((m) => {
            const colIndex = sheetColumns.indexOf(m.sheetColumn);
            const cellValue = row[colIndex];
            return {
              value: cellValue !== null && cellValue !== undefined ? cellValue : ''
            };
          });
        excelData.push(mappedRow);
      });

      // Return Excel buffer
      const buffer = await writeXlsxFile([excelData], {
        sheets: ['Data'],
      });

      return buffer;
    } catch (error) {
      console.error('Error generating Excel file:', error);
      throw error;
    }
  };

  // Create File object from filtered data
  const createFileFromFilteredData = async () => {
    try {
      const rows = [];

      // Add headers
      const headers = columnMapping
        .filter((m) => m.sheetColumn)
        .map((m) => m.parameter);
      rows.push(headers);

      // Add data
      filteredData.forEach((row) => {
        const mappedRow = columnMapping
          .filter((m) => m.sheetColumn)
          .map((m) => {
            const colIndex = sheetColumns.indexOf(m.sheetColumn);
            return row[colIndex] !== null && row[colIndex] !== undefined ? row[colIndex] : '';
          });
        rows.push(mappedRow);
      });

      // Convert to Excel buffer using writeXlsxFile
      const excelRows = rows.map(row =>
        row.map(cell => ({ value: cell }))
      );

      // Use a temporary approach - write to file system then read
      // Since writeXlsxFile's browser version directly downloads, we need a workaround

      // Create CSV as fallback (simpler)
      const csvContent = rows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], `Bulk_Template_${templateData?.name}_${Date.now()}.csv`, {
        type: 'text/csv',
      });

      return file;
    } catch (error) {
      console.error('Error creating file:', error);
      throw error;
    }
  };

  // Upload file to backend
  const handleUploadAndSend = async () => {
    setSending(true);
    setUploadProgress(0);
    setUserCount(0);
    setQueueCount(0);

    try {
      // Generate processed Excel file with mapped columns
      const excelBuffer = await generateFilteredExcelFile(filteredData);
      const processedFile = new File([excelBuffer], `Bulk_Template_${templateData?.name}_${Date.now()}.xlsx`, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const formData = new FormData();
      formData.append('file', processedFile);

      const onUploadProgress = (progressEvent) => {
        const { loaded, total } = progressEvent;
        const percent = Math.floor((loaded * 100) / total);
        setUploadProgress(percent);
      };

      const uploadResponse = await actions.uploadUserData(
        formData,
        templateData,
        onUploadProgress
      );

      if (uploadResponse?.status === 200) {
        const uploadedCount = uploadResponse?.data?.data?.updated_count || 0;
        const queuedCount = uploadResponse?.data?.data?.queue_count || 0;
        setUserCount(uploadedCount);
        setQueueCount(queuedCount);
        showSnackBar(`File uploaded successfully. ${uploadedCount} users ready to send.`, 'success');
        await sendTemplatesToQueue();
      } else if (uploadResponse?.status === 400) {
        const errorMsg = uploadResponse?.response?.data?.data?.general_error?.errors || 'Failed to upload data';
        showSnackBar(errorMsg, 'error');
      } else {
        showSnackBar('Failed to upload file', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackBar('Error uploading file: ' + error.toString(), 'error');
    } finally {
      setSending(false);
    }
  };
  // Send templates via cron endpoint
  const sendTemplatesToQueue = async () => {
    setIsSendingTemplates(true);

    if (!templateData?.wa_Id) {
      showSnackBar('Template ID is missing', 'warning');
      return;
    }

    try {
      const sendResponse = await actions.sendTemplateCron(templateData?.wa_Id);

      if (sendResponse?.status === 200) {
        showSnackBar(
          `Templates sent successfully! ${queueCount || userCount} messages queued.`,
          'success'
        );
        setShowSendDialog(false);
      } else {
        showSnackBar('Error occurred while sending templates. Please try again!', 'error');
      }
    } catch (error) {
      console.error('Error sending templates:', error);
      showSnackBar('Error sending templates: ' + error.toString(), 'error');
    } finally {
      setIsSendingTemplates(false);
    }
  };

  // Prepare columns for valid records table - USE PARAMETER NAMES
  const validRecordsColumns = columnMapping
    .filter((m) => m.sheetColumn)
    .map((mapping, index) => ({
      accessorKey: `col_${index}`,
      header: mapping.parameter,
      size: 150,
      Cell: ({ row }) => {
        const colIndex = sheetColumns.indexOf(mapping.sheetColumn);
        const value = row.original.rowData[colIndex];

        const isYellowRow = rowsWithAddedCode.includes(row.original.originalIndex);
        const isPhoneColumn = mapping.parameter === 'phone_number';

        return (
          <Box
            sx={{
              backgroundColor: isYellowRow && isPhoneColumn ? '#fff9c4' : 'transparent',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
          >
            <Typography variant="body2" noWrap>
              {value ? String(value) : '-'}
            </Typography>
          </Box>
        );
      },
    }));

  const validRecordsData = filteredData.map((row, index) => ({
    rowData: row,
    originalIndex: index,
  }));

  const invalidRecordsColumns = [
    {
      accessorKey: 'rowNumber',
      header: 'Original Row #',
      size: 120,
      Cell: ({ row }) => (
        <CommonBadges value={row.original.rowNumber} color="error" pill={true} />
      ),
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Phone Number',
      size: 150,
      Cell: ({ row }) => (
        <Typography variant="body2" color="error.main" fontWeight={600}>
          {row.original.phoneNumber ? String(row.original.phoneNumber) : 'N/A'}
        </Typography>
      ),
    },
    {
      accessorKey: 'reason',
      header: 'Reason',
      size: 200,
      Cell: ({ row }) => (
        <Typography variant="caption" color="error.main">
          {row.original.reason}
        </Typography>
      ),
    },
    ...columnMapping
      .filter((m) => m.sheetColumn && m.parameter !== 'phone_number')
      .map((mapping) => ({
        accessorKey: mapping.parameter,
        header: mapping.parameter,
        size: 150,
        Cell: ({ row }) => {
          const sheetColIndex = sheetColumns.indexOf(mapping.sheetColumn);
          return (
            <Typography variant="body2">
              {row.original.row[sheetColIndex] ? String(row.original.row[sheetColIndex]) : '-'}
            </Typography>
          );
        },
      })),
  ];

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Verify & Send Bulk Templates
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Review validated data and apply filters before sending bulk templates.
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h4" color="primary.main" fontWeight={600}>
                {verifiedData.length + invalidRows.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Uploaded
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h4" color="success.main" fontWeight={600}>
                {verifiedData.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valid Records
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h4" color="error.main" fontWeight={600}>
                {invalidRows.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invalid Records
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h4" color="info.main" fontWeight={600}>
                {filteredData.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                After Filters
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      {Object.keys(uniqueValues).length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Filter Records</Typography>
            <Button variant="outlined" startIcon={<Refresh />} onClick={resetFilters} size="small">
              Reset Filters
            </Button>
          </Box>

          <Grid container spacing={2}>
            {Object.keys(uniqueValues).map((parameter) => (
              <Grid item xs={12} sm={6} md={4} key={parameter}>
                <FormControl fullWidth size="small">
                  <InputLabel>{parameter}</InputLabel>
                  <Select
                    value={fieldFilters[parameter] || ''}
                    label={parameter}
                    onChange={(e) => handleFieldFilterChange(parameter, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {uniqueValues[parameter].map((value, idx) => (
                      <MenuItem key={idx} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Valid Records Table */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Valid Records Preview ({filteredData.length} records)
          </Typography>
          {rowsWithAddedCode.length > 0 && (
            <CommonBadges
              value={`${rowsWithAddedCode.length} rows with 91 added (shown in yellow)`}
              color="#ffd54f"
              pill={true}
            />
          )}
        </Box>

        <CommonTable
          columns={validRecordsColumns}
          data={validRecordsData}
          enableColumnFilters={false}
          enableHiding={true}
          enableDensityToggle={true}
          enableFullScreenToggle={true}
          enableTopToolbar={true}
          enableBottomToolbar={true}
          muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 400px)' } }}
        />
      </Box>

      {/* Invalid Records Table */}
      {invalidRows.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'error.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Warning sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" color="error.main">
                  Invalid Records - Not Included in Send ({invalidRows.length} rows)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  These rows have invalid phone numbers and will NOT be included in the bulk send.
                  Please fix these numbers and re-upload if you want to include them.
                </Typography>
              </Alert>

              <CommonTable
                columns={invalidRecordsColumns}
                data={invalidRows}
                enableColumnFilters={false}
                enableHiding={true}
                enableDensityToggle={true}
                enableFullScreenToggle={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                muiTableContainerProps={{ sx: { maxHeight: '400px' } }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Send Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LoadingButton
          buttonName={`Send to ${filteredData.length} Recipients`}
          onClickButton={() => setShowSendDialog(true)}
          isDisabled={filteredData.length === 0}
          isLoading={sending || isSendingTemplates}
          icon={<Send />}
        />
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showSendDialog} onClose={() => !sending && setShowSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Send</DialogTitle>
        <DialogContent>
          {!sending && !isSendingTemplates ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to send templates to <strong>{filteredData.length}</strong>{' '}
                recipients. This action cannot be undone.
              </Alert>
              <Typography variant="body2" gutterBottom>
                <strong>Template:</strong> {templateData?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Valid Recipients:</strong> {filteredData.length}
              </Typography>
              {invalidRows.length > 0 && (
                <Typography variant="body2" gutterBottom color="error.main">
                  <strong>Invalid/Skipped:</strong> {invalidRows.length}
                </Typography>
              )}
            </>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                {uploadProgress < 100 ? 'Uploading file...' : 'Sending templates...'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {uploadProgress}%
              </Typography>
              {userCount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Users Ready:</strong> {userCount}
                  </Typography>
                  {queueCount > 0 && (
                    <Typography variant="body2" color="success.main">
                      <strong>Queued for Sending:</strong> {queueCount}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!sending && !isSendingTemplates ? (
            <>
              <Button onClick={() => setShowSendDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={handleUploadAndSend}
              >
                Confirm & Send
              </Button>
            </>
          ) : (
            <Button disabled>Processing...</Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FilterStep;